"use client";

import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import type { LoginResponse } from "@/lib/auth/loginUser";
import loginUser from "@/lib/auth/loginUser";
import { LoginFormProperties, SwitchCallback } from "../types";
import { sendUserAccountAuthenticatedClient } from "@/lib/segment/logistration";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
  PageNames,
} from "@/constants/tagular/main";
import { useInView } from "react-intersection-observer";
import {
  getCorrelationID,
  pageTrackingObjectUpdate,
} from "@/utils/tagular/helpers";
import RedirectLogistration from "@/app/[locale]/auth/login/components/RedirectLogistration";
import Icon from "@/components/icons";
import AlertBox from "@/app/[locale]/auth/login/components/UI/AlertBox";
import { cleanUpHtml } from "@/app/[locale]/auth/login/components/utils";
import { Button } from "@/components/ui/button";
import useLogistrationPageRedesign from "@/monarch/rules/logistrationPageRedesign/useLogistrationPageRedesign";
import useFormEvents from "@/hooks/eventing/useFormtEvents";
import debounce from "@/utils/debounce";
import { getEnv } from "@/env";
import FormSwitchLink from "./FormSwitchLink";
import { useModalState } from "@/contexts/AuthModalProvider";
import { getUserInfoFromJWT } from "@/lib/auth/getUserInfo";

export default function LoginForm({
  currentProvider,
  finishAuthUrl,
  passwordResetComplete,
  switchToPasswordRecovery,
  switchToRegistration,
  isModal = false,
  onClose,
  updateLoggedInStatus,
}: LoginFormProperties & {
  switchToRegistration: SwitchCallback;
  onClose?: () => void;
  updateLoggedInStatus?: () => void;
}) {
  const { modalCallback } = useModalState();
  const baseUrl = getEnv("NEXT_PUBLIC_SITE_URL", "https://www.edx.org");
  const [error, setError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [loginResponse, setLoginResponse] = useState<LoginResponse | null>(
    null,
  );
  const [emailValue, setEmailValue] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { data } = useLogistrationPageRedesign();
  const loginInfo = data?.loginInfo;
  const loginTitle = loginInfo?.formTitle || "";
  const loginCta = loginInfo?.formCta || "";

  useEffect(() => {
    pageTrackingObjectUpdate(PageNames.SignIn, true);
  }, []);

  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: "sign-in-click",
      location: "login",
      name: "sign-in",
      text: "Sign In",
    },
  });

  const { elementClicked: switchToRegistrationClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Link,
      location: "login-page",
      name: "registration-redirect",
      text: "Register now",
    },
  });

  const { elementClicked: forgotPasswordClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: "forgot-password-click",
      location: "login",
      name: "forgot-password",
      text: "Forgot Password",
    },
  });

  const { elementViewed: errorMessageViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      htmlId: "message-banner",
      location: "login",
      name: "log-in-banner",
      text: "Error Message",
    },
  });

  const { fieldInputted } = useFormEvents({
    formContext: {
      formBrand: `${baseUrl}/auth/login`,
      formName: "Login",
      formType: "Login",
    },
  });

  const { ref: loginRef } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    setError("");

    const result = await loginUser(email, password);
    if (result.success && result.data.success) {
      const jwt = getUserInfoFromJWT();
      const userId = jwt?.user_id ?? jwt?.sub;
      if (userId && window?.analytics?.identify) {
        window.analytics.identify(String(userId));
      }
      setLoginResponse(result);
      setLoginSuccess(true);
      sendUserAccountAuthenticatedClient();
      elementClicked();
      if (isModal) {
        onClose?.();
        updateLoggedInStatus?.();
        modalCallback?.();
      }
    } else if (!result.success) {
      errorMessageViewed({
        webElement: { text: cleanUpHtml(result.errorMessage) },
      });
      setError(cleanUpHtml(result.errorMessage));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPasswordClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    forgotPasswordClicked();
    switchToPasswordRecovery(e, isModal);
  };

  const debouncedFieldInput = useMemo(
    () =>
      debounce((fieldName: string) => {
        fieldInputted({
          correlationId: getCorrelationID(),
          userInputField: {
            fieldId: fieldName,
            fieldLabel: fieldName,
            fieldName: fieldName,
            fieldType: fieldName,
            fieldValue: "SANITIZED",
          },
        });
      }, 500),
    [fieldInputted],
  );

  return (
    <div className="w-full md:max-w-[628px] flex flex-col gap-6">
      {loginTitle && !isModal && (
        <h2 className="md:text-5xl text-3xl my-0 italic font-black">
          {loginTitle}
        </h2>
      )}
      {!isModal && (
        <RedirectLogistration
          redirectUrl={
            loginResponse && loginResponse.success
              ? loginResponse.data.redirect_url || ""
              : ""
          }
          finishAuthUrl={finishAuthUrl}
          currentProvider={currentProvider}
          success={loginSuccess}
          cohesionCallback={elementClicked}
        />
      )}

      {passwordResetComplete && !error && (
        <AlertBox
          message="Your password has been reset. Sign in to your account."
          title="Password reset complete."
          type="message"
        />
      )}
      {error && <AlertBox type="error" title="" message={error} />}

      <form
        onSubmit={handleSubmit}
        ref={formRef}
        className="flex flex-col gap-2"
      >
        {/* Email Field */}
        <div>
          <label className="text-md font-bold text-gray-800" htmlFor="email">
            Username or email
          </label>
          <input
            id="email"
            type="text"
            className="w-full px-4 py-[9px] mb-0 border text-sm text-gray-700 box-border focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 rounded-[6px]"
            placeholder=""
            name="email"
            required
            autoComplete="username"
            value={emailValue}
            onChange={(e) => {
              setEmailValue(e.target.value);
            }}
            onBlur={() => {
              debouncedFieldInput("email");
            }}
          />
        </div>
        {/* Password Field */}
        <div>
          <label className="text-md font-bold text-gray-800" htmlFor="password">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              className="w-full px-4 py-[9px] mb-0 border text-sm text-gray-700 box-border focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 rounded-[6px]"
              placeholder=""
              name="password"
              required
              autoComplete="current-password"
              value={passwordValue}
              onChange={(e) => {
                setPasswordValue(e.target.value);
              }}
              onBlur={() => {
                debouncedFieldInput("password");
              }}
            />
            <button
              type="button"
              className="absolute right-3 top-[9px] bottom-[9px] flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={togglePasswordVisibility}
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <Icon
                  type="visibility-off"
                  svgProps={{ width: 24, height: 24 }}
                />
              ) : (
                <Icon type="visibility" svgProps={{ width: 24, height: 24 }} />
              )}
            </button>
          </div>
        </div>
        <div className={`flex mt-2 ${isModal ? "flex-col gap-2" : ""}`}>
          <Button
            ref={loginRef}
            type="submit"
            className={`text-md ${isModal ? "w-full" : ""}`}
            variant="brand"
          >
            {loginCta || "Sign in"}
          </Button>
          <a
            className={`text-md text-gray-800 font-medium py-2 hover:underline cursor-pointer ${
              isModal ? "underline px-0" : "text-center px-4"
            }`}
            onClick={handleForgotPasswordClick}
          >
            Forgot password
          </a>
        </div>
      </form>
      {!isModal && (
        <FormSwitchLink
          prompt="Don't have an account?"
          linkText="Register now"
          onSwitch={switchToRegistration}
          isModal={isModal}
          onAnalyticsClick={switchToRegistrationClicked}
        />
      )}
    </div>
  );
}
